import {DOM} from "../DOM";
import {StringValidator} from "./StringValidator";
import {StringValidatorValidationType} from "../../definitions/utils/validator/stringValidator/StringValidatorValidationType";
import {ValidatorDNSInput} from "../../definitions/utils/validator/validatorDNS/ValidatorDNSInput";
import {ValidatorDNS} from "./ValidatorDNS";
import {ElementValidatorUtil} from "./ElementValidatorUtil";
import {FormValidatorParams} from "../../definitions/utils/validator/validator/ValidateFormOptions";
import {DnsValidationType} from "../../definitions/utils/validator/validator/DnsValidationType";
import {DnsTypes} from "../../definitions/utils/validator/validator/DnsTypes";
import {Notify} from "../Notify";

/**
 * Check the input element and validate it
 * Use the validation options appended to input elements
 */
export class InputValidator {

    public static validateInput(input: HTMLInputElement | HTMLTextAreaElement, isUpdate: boolean|null, formOptions: FormValidatorParams) : void {

        const params = ElementValidatorUtil.getValidationParams(input);

        if (params.regexes) { this.validateRegexes(input, params.regexes); }
        if (params.customMaxLength) { this.validateMaxLength(input, params.customMaxLength); }
        if (params.minLength) { this.validateMinLength(input, params.minLength); }
        if (params.filled) { this.validateFilled(input, params.filled); }
        if (params.filledOneOf) { this.validateOneOfFilled(input, params.filledOneOf, isUpdate); }
        if (params.email) { this.validateEmail(input, params.email); }
        if (params.equalControlName) { this.validateEqualControlName(input, params.equalControlName); }
        if (params.maxFileSize) { this.validateMaxFileSize(input as HTMLInputElement, params.maxFileSize);}
        if (params.maxNumber) { this.validateMaxNumber(input, params.maxNumber); }
        if (params.minNumber) { this.validateMinNumber(input, params.minNumber); }
        if (params.dnsValidation) { this.validateDns(input, formOptions, params.dnsValidation, isUpdate); }
        if (params.stringValidation) { this.validateStringValidation(input, params.stringValidation); }
        if (params.date) { this.validateDate(input, params.date); }
    }

    /**
     * Validate DATE function
     * Check is correct date without time
     * Check future date if requested
     * @param element - validated input element or textarea
     * @param params - info about validation
     * @private
     */
    private static validateDate(element: HTMLInputElement|HTMLTextAreaElement, params: {onlyFuture?: boolean, problem: string|null}) : void {

        const value = element.value.trim();
        if (value) {

            const valid = StringValidator.validateString(value, {name: StringValidatorValidationType.DATE, meta: null});
            const validFuture = StringValidator.validateString(value, {name: StringValidatorValidationType.DATE_FUTURE, meta: null});
            const problemFuture = DOM.Parser.getDataByNameFromElement('trsFormsDateAfter', DOM.Parser.getHtmlElement('jsTranslations'));

            if (!valid || (params.onlyFuture && !validFuture)) {
                element.dataset.valid = "false";
                ElementValidatorUtil.updateHelpElement(element, (params.onlyFuture && !validFuture) ? problemFuture : params.problem, false);
                return;
            }
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate PATTERN function
     * Check if value is correct for used pattern
     * @param element - validated input element or textarea
     * @param regexes - list of regexes to validate
     */
    private static validateRegexes(element: HTMLInputElement | HTMLTextAreaElement, regexes: Array<{regex: RegExp; problem: string|null}>) {

        if (regexes) {
            for (let i = 0; i < regexes.length; i++) {

                const value = element.value.trim();
                if (value && !regexes[i].regex.test(value)) {
                    element.dataset.valid = "false";
                    ElementValidatorUtil.updateHelpElement(element, regexes[i].problem, false);
                    return;
                }

                ElementValidatorUtil.updateHelpElement(element, regexes[i].problem, true);
            }
        }
    }

    /**
     * Validate MAX LENGTH function
     * Check if value is in correct length
     * @param element - validated input element or textarea
     * @param params - params to validate max length
     */
    private static validateMaxLength(element: HTMLInputElement | HTMLTextAreaElement, params: {length: number, problem: string|null}) {

        if (!StringValidator.validateString(element.value, {name: StringValidatorValidationType.MAX_LENGTH, meta: params.length})) {
            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);
            return;
        }
        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate MIN LENGTH function
     * Check if value is in correct length
     * @param element - validated input element or textarea
     * @param params - params to check min length
     */
    private static validateMinLength(element: HTMLInputElement | HTMLTextAreaElement, params: {length: number, problem: string|null}) {

        if (!StringValidator.validateString(element.value, {name: StringValidatorValidationType.MIN_LENGTH, meta: params.length})) {
            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);
            return;
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate FILLED function
     * Check if value is filled
     * @param element - validated input element or textarea
     * @param params - params to check is filled
     */
    private static validateFilled(element: HTMLInputElement | HTMLTextAreaElement, params: {filled: boolean, problem: string|null}) {

        let valid = element.getAttribute('type') === 'checkbox'
            ? (element as HTMLInputElement).checked
            : StringValidator.validateString(element.value, {name: StringValidatorValidationType.REQUIRED, meta: null});

        if (!valid) { element.dataset.valid = "false"; }
        ElementValidatorUtil.updateHelpElement(element, params.problem, valid);
    }

    /**
     * Validate FILLED ONE OF function
     * Check if one of inputs is filled
     * @param element - validated input element or textarea
     * @param params - params to check
     * @param isUpdate - don't dispatch event
     */
    private static validateOneOfFilled(
        element: HTMLInputElement | HTMLTextAreaElement,
        params: {inputNames: Array<string>, problem: string|null},
        isUpdate: boolean|null) {

        let someIsFilled = false;
        for (const inputName of params.inputNames) {
            const currentInput = DOM.Parser.getHtmlInputElementByName(inputName) as HTMLInputElement;
            someIsFilled = currentInput.type === 'checkbox'
                ? currentInput.checked
                : StringValidator.validateString(currentInput.value, {name: StringValidatorValidationType.REQUIRED, meta: null});
            if (someIsFilled) { break; }
        }

        if (!someIsFilled) {
            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);
        } else {
            ElementValidatorUtil.updateHelpElement(element, params.problem, true);
        }

        if (!isUpdate) {
            params.inputNames.forEach((inputName: string) => {
                const currentInput = DOM.Parser.getHtmlInputElementByName(inputName) as HTMLInputElement;
                currentInput.dispatchEvent(new Event('update'))
            });
        }
    }

    /**
     * Validate EMAIL function
     * Check if value is correct email
     * @param element - validated input element or textarea
     * @param params - params to check
     */
    private static validateEmail(element: HTMLInputElement | HTMLTextAreaElement, params: {email: boolean, problem: string|null}) {

        const value = element.value.trim();
        if (value && value.includes(',')) {
            const emails = value.split(",");
            emails.forEach(function (email: string) {
                const valid = StringValidator.validateString(email, {name: StringValidatorValidationType.EMAIL, meta: null});
                if (!valid || email.length === 0) {
                    element.dataset.valid = "false";
                    ElementValidatorUtil.updateHelpElement(element, params.problem, false);
                    return;
                }
            });
        } else if (value) {
            const valid = StringValidator.validateString(value, {name: StringValidatorValidationType.EMAIL, meta: null});
            if (!valid) {
                element.dataset.valid = "false";
                ElementValidatorUtil.updateHelpElement(element, params.problem, false);
                return;
            }
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate EQUAL to value of another input with name
     * Check if value of one input is equal to another
     * @param element - validated input element or textarea
     * @param params - params to check
     */
    private static validateEqualControlName(element: HTMLInputElement | HTMLTextAreaElement, params: {name: string, problem: string|null}) {

        const referenceElement = DOM.Parser.getHtmlInputElementByName(params.name) as HTMLInputElement;
        const referenceElementValue = referenceElement.value.trim();
        const elementValue = element.value.trim();
        if (referenceElementValue !== elementValue) {
            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);
            return;
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate MIN LENGTH function
     * Check if value is in correct length
     * @param element - validated input element or textarea
     * @param params - params to check
     */
    private static validateMaxFileSize(element: HTMLInputElement, params: {maxSize: number, problem: string|null}) {

        if (!element.files || typeof element.files[0] === 'undefined') {
            return;
        }

        const size = Array.from(element.files).reduce((acc: number, value: File) => acc + value.size, 0);
        if (size > params.maxSize) {

            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);

            const jsTranslations = DOM.Parser.getHtmlElement('jsTranslations');

            Notify.Handler.processMessages([{
                message: DOM.Parser.getDataByNameFromElement('trsFormsMaxFilesize', jsTranslations),
                type: "warning",
                closeModal: null,
                uniqKeys: null,
                eventId: null
            }]);

            element.value = "";
            element.dispatchEvent(new Event('change'));
            return;
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate MAX NUMBER function
     * Check if value is in below max
     * @param element - validated input element or textarea
     * @param params - params to check
     */
    private static validateMaxNumber(element: HTMLInputElement | HTMLTextAreaElement, params: {max: number, problem: string|null}) {

        const value = element.value.trim();
        if (parseInt(value) > params.max) {
            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate MIN NUMBER function
     * Check if value is above min
     * @param element - validated input element or textarea
     * @param params - params to check
     */
    private static validateMinNumber(element: HTMLInputElement | HTMLTextAreaElement, params: {min: number, problem: string|null}) {

        const value = element.value.trim();
        if (parseInt(value) < params.min) {
            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate DNS string input using type value
     * @param element - validated input element
     * @param options - form validation options
     * @param params - validation options
     * @param isUpdate
     */
    private static validateDns(element: HTMLInputElement | HTMLTextAreaElement, options: FormValidatorParams, params: DnsValidationType, isUpdate: boolean|null) {

        const dnsTypeSelect = DOM.Parser.getHtmlInputElementByName(params.typeInputName) as HTMLSelectElement;
        const dnsType = dnsTypeSelect.value as DnsTypes;
        const value = element.value.trim();
        const patternTypes = params.patterns[dnsType];
        if (!patternTypes) { return; }

        let iterablePatterns : ValidatorDNSInput[];
        if (typeof patternTypes === 'object' && !Array.isArray(patternTypes)) {
            iterablePatterns = Object.entries(patternTypes).map((item) => {
                return {name: item[0], meta: item[1] ?? null};
            }) as ValidatorDNSInput[];
        } else {
            iterablePatterns = patternTypes.map((item) => {
                return {name: item, meta: null};
            }) as ValidatorDNSInput[];
        }

        for (const input of iterablePatterns) {
            if (!ValidatorDNS.validate(value, input, params, element, options.form, isUpdate)) {
                element.dataset.valid = "false";
                ElementValidatorUtil.updateHelpElement(element, params.problem, false);
                return;
            }
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }

    /**
     * Validate string using string validator
     * @param element - validated input element
     * @param params - check params
     */
    private static validateStringValidation(element: HTMLInputElement | HTMLTextAreaElement, params: {patternType: StringValidatorValidationType, problem: string|null}) {

        const value = element.value.trim();
        if (!StringValidator.validateString(value, {name: params.patternType, meta: null})) {
            element.dataset.valid = "false";
            ElementValidatorUtil.updateHelpElement(element, params.problem, false);
            return;
        }

        ElementValidatorUtil.updateHelpElement(element, params.problem, true);
    }
}


